import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { usePrimaryRowComparison } from '~/hooks/viewer/usePrimaryRowComparison';
import { useRegistrationComparison } from '~/hooks/viewer/useShowRegistrationComparison';
import { replanningCompareViewState } from '~/state/atoms/adaptiveReplanningAtoms';
import type { ReplanningStage } from './replanningStages';

export function useStagePresentation(
    presentation: ReplanningStage['presentation'],
    hasResult = true
): void {
    const comparing = useRecoilValue(replanningCompareViewState);
    const comparison = useRegistrationComparison();
    const primaryRow = usePrimaryRowComparison();

    // Held, not depended on: the comparison is a fresh object every render.
    const apply = useRef({ comparison, primaryRow });
    useEffect(() => {
        apply.current = { comparison, primaryRow };
    });

    const resampledUid =
        presentation === 'blend' && !comparing
            ? comparison?.resampled?.seriesUid
            : undefined;
    const showsSingleScan =
        presentation === 'single' && !comparing && hasResult;

    useEffect(() => {
        const resampled = apply.current.comparison?.resampled;
        if (!resampledUid || !resampled) {
            return;
        }
        // Same frame as the primary image, so the blend needs no registration.
        apply.current.primaryRow.show(resampled, undefined, 'blend');
        return () => apply.current.primaryRow.end(resampled);
    }, [resampledUid]);

    useEffect(() => {
        const paired = apply.current.primaryRow.pairedImage;
        if (showsSingleScan && paired) {
            apply.current.primaryRow.end(paired);
        }
    }, [showsSingleScan]);
}
